















































































import Vue from 'vue';
import Component from 'vue-class-component';
import {Prop} from 'vue-property-decorator';
import BindAttrs from '@/utils/binds-attrs';
import {AMETRAS_MIME_LOOKUP_INST} from '@/utils/mime.service';
import {HTTP_SERVICE_INSTANCE} from '@/services/http.service';
import AmetrasLabelTemplate from '@/fields/AmetrasLabelTemplate.vue';
import AdvancedConfirmDialog, {ConfirmationDialogButton, ConfirmationDialogConfig} from '@/views/AdvancedConfirmDialog.vue';
import '@/compiled-icons/download';
import {DisguiseBusinessExceptionWrapper} from '@/exception/disguise-business-exception-wrapper';

interface UploadFile {
  name: string;
  progress: number;
  indeterminate: boolean;
  uploaded: boolean;
  id?: string;
  icon: string;
}

/**
 * General Component for Fields of type Upload
 *
 */
@Component({
  components: {AdvancedConfirmDialog, AmetrasLabelTemplate},
  mixins: [BindAttrs]
})
export default class AmetrasImportTemplate extends Vue {
  @Prop({
    required: true
  }) max!: number;
  @Prop() label!: string;
  @Prop() readonly!: boolean;

  @Prop({
    required: false,
    default: false
  }) updateMode!: boolean;

  @Prop() typeSpecificSettings!: DisguiseDto.ImportFieldSpecificSettings;

  dragOver: boolean = false;
  existingFilesLoading: boolean = false;
  readableMaxSize: string = '';
  files: UploadFile[] = [];

  confirmationDialogConfig: ConfirmationDialogConfig | null = null;

  importDone = false;
  importResponse: DisguiseDto.ImportResponseDto | null = null;
  importLoading = false;

  $refs!: {
    selectFile: HTMLInputElement
  };

  created() {
    let readableMaxSize = '';
    if (this.max > 1024 * 1024) {
      readableMaxSize = Math.round(this.max / (1024 * 1024)) + ' MB';
    } else if (this.max > 1024) {
      readableMaxSize = Math.round(this.max / (1024)) + ' KByte';
    } else {
      readableMaxSize = Math.round(this.max) + ' Byte';
    }
    this.readableMaxSize = readableMaxSize;
  }

  /**
   * The user selected files via file chooser
   */
  fileSelected() {
    const selectedFiles: FileList | null = this.$refs.selectFile.files;
    if (selectedFiles) {
      this.uploadFiles(selectedFiles);
    }
  }

  downloadFile() {
    const downloadUrl = '/import/v0/error/download?importId=' + this.importResponse?.importId;
    window.open(downloadUrl);
  }

  importFile() {
    const settings = this.typeSpecificSettings as  DisguiseDto.ImportFieldSpecificSettings;
    const fileUUid = this.files[0].id;
    const callUrl = `${settings.url}?fileUUID=` + fileUUid;

    this.importLoading = true;

    HTTP_SERVICE_INSTANCE.getAxios().get(callUrl).then((response) => {
      this.importLoading = false;
      this.importDone = true;
      this.importResponse = (response.data as DisguiseDto.ImportResponseDto);
      this.$store.commit('showSnack', {
        message: 'Import Erfolgreich',
        color: 'success'
      });
    }, (error: any) => {
      this.importLoading = false;
      if (!(error instanceof DisguiseBusinessExceptionWrapper)) {
        this.$store.commit('showSnack', {
          message: 'Übernahme fehlgeschlagen',
          color: 'error'
        });
      }
    });
  }

  /**
   * The user dropped files via DnD
   */
  dropFiles(dropEvent: DragEvent) {
    const droppedFiles: FileList | undefined = (dropEvent.dataTransfer || {}).files;
    if (droppedFiles) {
      this.uploadFiles(droppedFiles);
    }
    this.dragOver = false;
  }

  /**
   * The user drags files over the file drop area
   */
  dragover(dropEvent: DragEvent) {
    this.dragOver = true;
  }

  /**
   * The user exits dragging files over the file drop area
   */
  dragexit(dropEvent: DragEvent) {
    this.dragOver = false;
  }

  /**
   * The user leaves the file drop area
   */
  dragleave(dropEvent: DragEvent) {
    this.dragOver = false;
  }

  /**
   * The user pressed delete button
   */
  deleteFile(file: UploadFile, event: Event) {
    event.preventDefault();

    this.confirmationDialogConfig = {
      title: 'Bestätigen',
      headerColor: 'primary',
      message: `Möchten Sie den Anhang \'${file.name}\' wirklich löschen?`,
      buttons: [{
        text: 'Bestätigen',
        title: 'Bestätigen',
        disabled: false,
        loading: false,
        autoFocus: true,
        onClick: (button: ConfirmationDialogButton) => {
          button.loading = true;
          button.disabled = true;
          file.uploaded = false;
          file.indeterminate = true;
          file.progress = 0;

          const deleteUrl = '/import/v0/file/delete?uuid=' + file.id;

          HTTP_SERVICE_INSTANCE.getAxios().delete(
              deleteUrl
          ).then(() => {
            this.files.splice(this.files.indexOf(file), 1);

            this.$store.commit('showSnack', {
              message: `\'${file.name}\' wurde gelöscht.`,
              color: 'success'
            });

            this.confirmationDialogConfig = null;
          }, () => {
            file.uploaded = true;
            this.$store.commit('showSnack', {
              message: `Löschen der Datei \'${file.name}\' fehlgeschlagen`,
              color: 'error'
            });

            button.loading = false;
            button.disabled = false;
          });
        }
      }, {
        text: 'Abbrechen',
        title: 'Abbrechen',
        disabled: false,
        loading: false,
        onClick: () => {
          this.confirmationDialogConfig = null;
        }
      }],
      onEsc: () => {
        this.confirmationDialogConfig = null;
      }
    };
  }

  /**
   * Internal upload handling
   */
  private uploadFiles(files: FileList) {
    for (const currentFile of files || []) {
      if (currentFile.size > this.max) {
        this.$store.commit('showSnack', {
          message: `Die Datei \'${currentFile.name}\' überschreitet die zulässige Dateigröße.`,
          color: 'error'
        });
      } else {
        const fileExtension = AMETRAS_MIME_LOOKUP_INST.extractExtension(currentFile.name);
        const fileIcon = AMETRAS_MIME_LOOKUP_INST.lookupIcon(fileExtension);

        const newUploadFile: UploadFile = {
          name: currentFile.name,
          progress: 0,
          uploaded: false,
          indeterminate: false,
          icon: fileIcon
        };
        this.files.push(newUploadFile);

        const formData: FormData = new FormData();
        formData.append('file', currentFile);

        HTTP_SERVICE_INSTANCE.getAxios().request<string>({
          method: (this.typeSpecificSettings || {}).updateMode ? 'put' : 'post',
          url: (this.typeSpecificSettings || {}).uploadServiceUrl || '/upload/api/v0/files',
          data: formData,
          headers: {
            Accept: 'text/plain',
          },
          onUploadProgress: (progressEvent: ProgressEvent) => {
            newUploadFile.progress = Math.round(progressEvent.loaded * 100 / progressEvent.total);
          }
        }).then((response) => {

          const uploadUuid = response.data;

          newUploadFile.progress = 100;
          newUploadFile.indeterminate = true;
          newUploadFile.id = uploadUuid;
          newUploadFile.uploaded = true;
          this.$emit('afterUploadCompleted', newUploadFile);
        }, () => {
          this.files.splice(this.files.indexOf(newUploadFile), 1);

          this.$store.commit('showSnack', {
            message: `Dateiupload der Datei \'${newUploadFile.name}\' fehlgeschlagen`,
            color: 'error'
          });
        });
      }
    }
  }
}
